import React from 'react';
import { classes, style } from 'typestyle';
import * as tabStyles from '../statistics-tabs.styles';
import * as styles from '../tabs/reporting-tab.styles';
import { type GroupReport } from '../models/dashboard.models';
import SortableHeaderCell from './sortable-header-cell.component';
import TablePagination from './table-pagination.component';
import { usePagedRows, useSortableRows } from './table-controls.hook';

/** As many rows as the rooms table shows per page. */
export const GROUPS_ROWS_PER_PAGE = 8;

const sortValue = (group: GroupReport, key: string): string | number => {
  switch (key) {
    case 'office':
      return group.officeName;
    case 'avgOccupancy':
      return group.averageOccupancyPercentage;
    case 'reservations':
      return group.reservations;
    case 'checkIns':
      return group.checkIns;
    case 'noShows':
      return group.noShows;
    case 'noShowRate':
      return group.noShowRatePercentage;
    case 'group':
    default:
      return group.groupName;
  }
};

interface GroupReportTableProps {
  readonly groups: GroupReport[];
  /** Whether the report spans several offices, so each group's office is worth naming. */
  readonly showOfficeColumn: boolean;
  readonly t: (key: string) => string;
}

/**
 * The reporting KPIs for each group, over its group bookings only. Starts busiest first, as the API sends
 * them; every column sorts, and the rows page like the rooms table. A group deleted since keeps its history,
 * marked as deleted.
 */
const GroupReportTable = ({ groups, showOfficeColumn, t }: GroupReportTableProps): React.JSX.Element => {
  const { sortedRows, sortKey, sortDirection, toggleSort } = useSortableRows<GroupReport>(groups, sortValue);
  const { pagedRows, page, setPage, pageCount } = usePagedRows(sortedRows, GROUPS_ROWS_PER_PAGE);

  const header = (label: string, key: string): React.JSX.Element => (
    <SortableHeaderCell
      className={style(styles.tableHeaderCell)}
      label={label}
      sortKeyName={key}
      activeSortKey={sortKey}
      sortDirection={sortDirection}
      onSort={toggleSort}
    />
  );

  return (
    <div className={style(styles.chartCard)}>
      <div className={style(styles.chartTitle)}>{t('reporting-groups-title')}</div>
      {groups.length === 0 ? (
        <span className={style(tabStyles.emptyMessage)}>{t('reporting-groups-empty')}</span>
      ) : (
        <div className={classes(styles.tableWrap, style({ marginBottom: 0 }))}>
          <table className={style(styles.table)}>
            <thead>
              <tr>
                {header(t('reporting-table-group'), 'group')}
                {showOfficeColumn && header(t('reporting-table-office'), 'office')}
                {header(t('reporting-kpi-avg-occupancy'), 'avgOccupancy')}
                {header(t('reporting-kpi-reservations'), 'reservations')}
                {header(t('reporting-table-checkins'), 'checkIns')}
                {header(t('reporting-kpi-no-shows'), 'noShows')}
                {header(t('reporting-kpi-no-show-rate'), 'noShowRate')}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((group) => (
                <tr key={group.groupId}>
                  <td className={style(styles.tableCell)}>
                    {group.groupName}
                    {group.isGroupDeleted && <span className={style(styles.deletedTag)}>{t('reporting-group-deleted')}</span>}
                  </td>
                  {showOfficeColumn && <td className={style(styles.tableCell)}>{group.officeName}</td>}
                  <td className={style(styles.tableCell)}>{group.averageOccupancyPercentage.toFixed(1)}%</td>
                  <td className={style(styles.tableCell)}>{group.reservations}</td>
                  <td className={style(styles.tableCell)}>{group.checkIns}</td>
                  <td className={style(styles.tableCell)}>{group.noShows}</td>
                  <td className={style(styles.tableCell)}>{group.noShowRatePercentage.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination page={page} pageCount={pageCount} onChangePage={setPage} />
        </div>
      )}
    </div>
  );
};

export default GroupReportTable;
